/**
 * In-memory, per-Excel-session L1 cache for FactSet TTF (Text-to-Formula)
 * results. Stores **only successful** `TtfApiResponse` payloads so a
 * repeat of the exact same natural-language query within a session
 * (very common when the LLM emits the same `ttfQuery` for symmetric
 * cells across a model) is a millisecond hit instead of a 1–4s upstream
 * round trip.
 *
 * **Why no negative cache?** Failures (timeout, network blip, transient
 * 5xx, malformed shape) are mostly non-deterministic. Caching them
 * would lock a query into the LLM-fallback path even after the upstream
 * recovers. The fallback already produces a working formula, so the
 * usual reason for negative caching ("don't re-experience breakage")
 * does not apply here. Reauth-required is handled separately via the
 * sticky-disable bit on `TtfRunContext` in `ttfCellDispatch.ts`.
 *
 * **Lifetime.** Module-level state lives for the duration of the
 * taskpane process — i.e. one Excel session. Excel restart clears the
 * cache. We also register with the shared FactSet client-session lifecycle
 * so a previous user's results never linger when someone reconnects as
 * a different FactSet user.
 *
 * **Capacity.** LRU at 200 entries — large enough to cover a typical
 * 100-cell model with healthy headroom, small enough that the eviction
 * cost stays trivial. No TTL today; capacity-based eviction plus the
 * session-scoped lifetime are sufficient.
 */

import type { TtfApiResponse } from "./spreadsheetIntents";
import { registerFactSetClientSessionClearHandler } from "../../../shared/factset/factsetSessionLifecycle";

const DEFAULT_CAPACITY = 200;

/** LRU cache for successful TTF responses. */
export class TtfCache {
  private readonly capacity: number;
  // Map preserves insertion order; we re-insert on `get` to mark recency,
  // and evict from the iteration head when over capacity.
  private readonly entries = new Map<string, TtfApiResponse>();

  constructor(capacity: number = DEFAULT_CAPACITY) {
    if (!Number.isFinite(capacity) || capacity <= 0) {
      throw new Error(`TtfCache capacity must be a positive integer, got ${capacity}`);
    }
    this.capacity = Math.floor(capacity);
  }

  /** Retrieves a cached response and refreshes its recency. */
  get(query: string): TtfApiResponse | null {
    if (!query) return null;
    const hit = this.entries.get(query);
    if (!hit) return null;
    // Refresh recency by re-inserting at the tail.
    this.entries.delete(query);
    this.entries.set(query, hit);
    return hit;
  }

  /** Inserts or refreshes a successful response. Evicts the LRU entry on overflow. */
  set(query: string, response: TtfApiResponse): void {
    if (!query) return;
    if (this.entries.has(query)) {
      this.entries.delete(query);
    } else if (this.entries.size >= this.capacity) {
      const oldestKey = this.entries.keys().next().value;
      if (oldestKey !== undefined) {
        this.entries.delete(oldestKey);
      }
    }
    this.entries.set(query, response);
  }

  /** Removes every entry. Called from the OAuth disconnect handler. */
  clear(): void {
    this.entries.clear();
  }

  /** Current entry count — exposed for telemetry / tests. */
  get size(): number {
    return this.entries.size;
  }
}

/**
 * Singleton instance used by the Excel cell-write path. Tests should
 * either construct their own `TtfCache` and inject it through
 * `tryDispatchCellViaTtf`'s `deps.cache` parameter, or call
 * `excelTtfCache.clear()` between cases.
 */
export const excelTtfCache = new TtfCache();

registerFactSetClientSessionClearHandler(() => {
  excelTtfCache.clear();
});
